// src/services/companyProgressService.js
//
// The company progress board's one read.
//
// ONE AGGREGATED READ (D1.12.1). Everything the board draws (activation,
// premium, billing, reputation, distribution, streak, achievements,
// recommendations, notices, nav, tiles) arrives in a single payload, so the
// board can never render two modules that disagree about which milestone the
// account is on.
//
// Axios through api.js, like every other authenticated /company/* read: the
// JWT comes off the request interceptor, so no caller ever passes a token.
//
// THE CHANGE SIGNAL IS RE-EXPORTED, NOT REDECLARED. Feed create, file import,
// the recruiter swipe and the publish all announce themselves through
// companyJourneyService's listener Set. A second Set here would leave this
// board deaf to every one of those writes while the old board still heard
// them, which is the worst of both: two boards on one page disagreeing.
import api from '../api';
import { handleApiError } from '../utils/errorHandler';

export {
  subscribeToJourneyChanges,
  notifyJourneyChanged,
} from './companyJourneyService';

const companyProgressService = {
  /**
   * This account's aggregated progress payload (`CompanyProgress`).
   *
   * Throws an Error carrying `.status` and `.response`, which is how a caller
   * tells the ADMIN 400 (`NO_GROUP`) apart from a real outage. The board shows
   * nothing on the first and a retry on the second; a bare thrown string would
   * collapse the two.
   */
  getProgress: async () => {
    try {
      const response = await api.get('/company/progress');
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Failed to load your progress');
    }
  },

  /**
   * Store the browser's IANA zone on the signed-in user (D1.13.1).
   *
   * THE BROWSER IS THE ONLY THING THAT KNOWS IT. Every streak boundary in the
   * system is drawn in the recruiter's own calendar day, and the server has no
   * other source for the zone: there is no timezone field in Settings, and the
   * fallback is America/New_York, which is wrong for most of the country.
   *
   * THE /auth/me CACHE IS CLEARED ON THE WAY OUT. api.js serves that read from
   * a short-TTL cache, and the dashboard decides whether to write at all from
   * `streak.timezoneMissing`, which is derived from the stored value. Without
   * the clear, the next read hands back the pre-patch body and the effect fires
   * again on the following mount, which is the loop the once-only guard exists
   * to prevent.
   */
  patchTimezone: async (timezone) => {
    if (!timezone) return null;
    try {
      const response = await api.patch('/auth/me', { timezone });
      api.invalidateCache('/auth/me');
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Failed to save your timezone');
    }
  },
};

export default companyProgressService;
